import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Inbox, Search, X, Eye, CheckCircle2, XCircle, Clock,
  Phone, Mail, Calendar, Building2, User, Loader2, Sparkles, Wallet, MapPin,
} from 'lucide-react';
import { adminApi } from '../services/api';
import EtatVide from '../components/EtatVide';
import PiedTableau from '../components/PiedTableau';
import {
  annoncerErreur, annoncerSucces, ChargeurLocal, EtatErreur, EtatSansDonnees, SqueletteTableau,
} from '../etats';

// ── Config badges ──────────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  formation:       { label: 'Formation',       className: 'bg-[var(--info-fond)] text-[var(--text-bold)] border-[var(--border)]' },
  accompagnement:  { label: 'Accompagnement',  className: 'bg-[var(--vert-pale)] text-[var(--vert-fonce)] border-[var(--border)]' },
  certification:   { label: 'Certification',   className: 'bg-[var(--info-fond)] text-[var(--text-bold)] border-[var(--border)]' },
};

const STATUT_CONFIG = {
  en_attente: { label: 'En attente', className: 'bg-[var(--attention-fond)] text-[var(--attention)] border-[var(--border)]', Icon: Clock },
  traite:     { label: 'Traitée',    className: 'bg-[var(--vert-pale)] text-[var(--vert-fonce)] border-[var(--border)]', Icon: CheckCircle2 },
  rejete:     { label: 'Rejetée',    className: 'bg-[var(--alerte-fond)] text-[var(--alerte)] border-[var(--border)]',       Icon: XCircle },
};

// ── Sous-composants ────────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const cfg = TYPE_CONFIG[type] || { label: type, className: 'bg-[var(--bg-main)] text-[var(--text-main)] border-[var(--border)]' };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-controle text-[12px] font-semibold border ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function StatutBadge({ statut }) {
  const cfg = STATUT_CONFIG[statut] || { label: statut, className: 'bg-[var(--bg-main)] text-[var(--text-main)] border-[var(--border)]', Icon: Clock };
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-controle text-[12px] font-semibold border ${cfg.className}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

// ── Modal détail demande ───────────────────────────────────────────────────────

function DemandeModal({ demande, onClose, onUpdate, onReload }) {
  const [notes, setNotes] = useState(demande.notes_admin || '');
  const [loading, setLoading] = useState(null);     // l'action en cours d'envoi
  const [error, setError] = useState('');

  // Accept flow state
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [orgSearch, setOrgSearch] = useState(demande.nom_organisation || '');
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [erreurListes, setErreurListes] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState('');
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);

  // Reponse au demandeur (e-mail)
  const [reponse, setReponse] = useState('');
  const [envoiReponse, setEnvoiReponse] = useState(false);
  const [reponses, setReponses] = useState(demande.reponses || []);
  const [infoReponse, setInfoReponse] = useState(null);

  const typeService = demande.type_service;
  const canAcceptDirectly = typeService === 'formation' || typeService === 'certification';
  /* Une certification demandée depuis l'espace d'une organisation accompagnée
     est rattachée à son projet : l'accepter ouvre la certification dans cet
     espace, sans organisation à choisir ni second dossier. */
  const rattachee = typeService === 'certification' && !!demande.projet && !demande.projet_en_attente;
  /* Une demande faite depuis l'espace de l'organisation a déjà son projet,
     en attente : l'accepter le démarre, sans organisation à choisir. */
  const depuisLEspace = !!demande.projet_en_attente;

  const handleDemarrerProjet = async () => {
    setLoading('accepter');
    setError('');
    try {
      await adminApi.accepterDemande(demande.id, { notes_admin: notes });
      annoncerSucces(`Demande acceptée : le projet de ${demande.nom_organisation || 'l’organisation'} démarre.`);
      onReload();
      onClose();
    } catch (e) {
      annoncerErreur(e.message || 'Le projet n’a pas pu être démarré.');
    } finally {
      setLoading(null);
    }
  };

  const loadAcceptData = async () => {
    setOrgsLoading(true);
    setErreurListes(false);
    try {
      const [orgsData, coachesData] = await Promise.all([
        adminApi.getOrganisations(),
        adminApi.getCoaches(),
      ]);
      setOrgs(Array.isArray(orgsData) ? orgsData : []);
      setCoaches(Array.isArray(coachesData) ? coachesData : []);
    } catch {
      setErreurListes(true);
    } finally { setOrgsLoading(false); }
  };

  // L'organisation de la demande etait pre-remplie dans le champ de recherche
  // mais jamais reellement selectionnee : le bouton de creation restait grise
  // sans explication. On la selectionne automatiquement des qu'elle est trouvee.
  const autoSelectionOrg = useCallback(() => {
    if (selectedOrg || !orgs.length) return;
    const cible = (demande.nom_organisation || '').trim().toLowerCase();
    if (!cible) return;
    const exact = orgs.find(o => (o.nom || '').trim().toLowerCase() === cible);
    const partiels = orgs.filter(o => (o.nom || '').toLowerCase().includes(cible));
    const trouve = exact || (partiels.length === 1 ? partiels[0] : null);
    if (trouve) { setSelectedOrg(trouve); setOrgDropdownOpen(false); }
  }, [orgs, selectedOrg, demande.nom_organisation]);

  useEffect(() => { autoSelectionOrg(); }, [autoSelectionOrg]);

  const handleRepondre = async () => {
    const texte = reponse.trim();
    if (!texte) { setInfoReponse({ type: 'err', text: 'Veuillez saisir un message.' }); return; }
    setEnvoiReponse(true);
    setInfoReponse(null);
    try {
      const res = await adminApi.repondreDemande(demande.id, texte);
      setReponses(prev => [...prev, res?.reponse || { message: texte, created_at: new Date().toISOString(), email_sent: !!res?.email_sent }]);
      setReponse('');
      setInfoReponse(res?.email_sent
        ? { type: 'ok', text: `Message envoye a ${res.recipient}.` }
        : { type: 'warn', text: `Message enregistre mais non envoye${res?.email_error ? ' (' + res.email_error + ')' : ''}.` });
    } catch (e) {
      setInfoReponse({ type: 'err', text: e.message || "Echec de l'envoi." });
    } finally {
      setEnvoiReponse(false);
    }
  };

  const handleOpenAccept = () => {
    setShowAcceptForm(true);
    loadAcceptData();
  };

  const filteredOrgs = orgs.filter(o =>
    (o.nom || '').toLowerCase().includes(orgSearch.toLowerCase())
  );

  const handleOuvrirCertification = async () => {
    setLoading('accepter');
    setError('');
    try {
      await adminApi.accepterDemande(demande.id, { notes_admin: notes });
      annoncerSucces(`Certification ouverte dans l’espace de ${demande.nom_organisation || 'l’organisation'}.`);
      onReload();
      onClose();
    } catch (e) {
      annoncerErreur(e.message || 'La certification n’a pas pu être ouverte.');
    } finally {
      setLoading(null);
    }
  };

  const handleAccepter = async () => {
    if (!selectedOrg) { setError("Veuillez sélectionner l'organisation."); return; }
    setLoading('accepter');
    setError('');
    try {
      await adminApi.accepterDemande(demande.id, {
        organisation_id: selectedOrg.id,
        coach_id: selectedCoach || null,
        notes_admin: notes,
      });
      annoncerSucces(`Demande acceptée : l’espace de ${selectedOrg.nom} est créé.`);
      onReload();
      onClose();
    } catch (e) {
      annoncerErreur(e.message || 'L’espace n’a pas pu être créé.');
    } finally {
      setLoading(null);
    }
  };

  const handleAction = async (statut) => {
    setLoading(statut);
    setError('');
    try {
      await onUpdate(demande.id, { statut, notes_admin: notes });
      annoncerSucces(statut === 'rejete' ? 'Demande rejetée.' : 'Demande marquée comme traitée.');
      onClose();
    } catch (e) {
      annoncerErreur(e.message || 'La demande n’a pas pu être mise à jour.');
    } finally {
      setLoading(null);
    }
  };

  const fmt = (v) => v ? new Date(v).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[var(--bg-card)] rounded-carte w-full max-w-2xl shadow-carte max-h-[90vh] flex flex-col"
      >
        {/* Header modal */}
        <div className="flex items-start justify-between p-6 border-b border-[var(--border)] shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <TypeBadge type={typeService} />
              <StatutBadge statut={demande.statut} />
              {ORIGINES[demande.origine] && (
                <span className="px-2 py-0.5 rounded-controle text-[11px] font-semibold border border-[var(--border)] text-[var(--text-muted)]">
                  {ORIGINES[demande.origine]}
                </span>
              )}
            </div>
            <h3 className="text-xl font-semibold text-[var(--text-bold)]">
              {demande.nom || '—'}
            </h3>
            <p className="text-sm text-[var(--text-muted)]">{demande.nom_organisation || '—'}</p>
          </div>
          <button
            onClick={onClose}
            className="act act-icone act-tertiaire shrink-0"
          >
            <X className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
        </div>

        {/* Corps scrollable */}
        <div className="overflow-y-auto flex-1 p-6 space-y-5">

          {/* ── Formule demandée ──────────────────────────────────────────
              Avant la page des tarifs, il fallait déduire la formule du
              message libre, ou la redemander. Elle arrive maintenant avec la
              demande, et c'est elle qui fondera le contrat : elle passe donc
              en tête, avant les coordonnées.

              Absente pour les demandes reçues par les anciens formulaires —
              le bloc disparaît alors plutôt que d'afficher un tiret. */}
          {demande.formule_display && (
            <div className="rounded-controle border border-[var(--accent)]/30 bg-[var(--accent)]/8 px-4 py-3">
              <p className="sous-titre-page">Formule demandée</p>
              <p className="text-[15px] font-semibold text-[var(--text-bold)] mt-1">
                {demande.formule_display}
              </p>
              {demande.formule_prix != null && (
                <p className="text-[13px] text-[var(--text-muted)] mt-0.5 tabular-nums">
                  {new Intl.NumberFormat('fr-FR').format(demande.formule_prix)}&nbsp;FCFA
                </p>
              )}
            </div>
          )}

          {/* Infos contact */}
          <div className="grid sm:grid-cols-2 gap-3">
            {demande.email && (
              <div className="flex items-center gap-3 bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                <Mail className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                <div>
                  <p className="sous-titre-page">Email</p>
                  <p className="text-sm font-semibold text-[var(--text-bold)]">{demande.email}</p>
                </div>
              </div>
            )}
            {demande.telephone && (
              <div className="flex items-center gap-3 bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                <Phone className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                <div>
                  <p className="sous-titre-page">Téléphone</p>
                  <p className="text-sm font-semibold text-[var(--text-bold)]">{demande.telephone}</p>
                </div>
              </div>
            )}
            {demande.date_demande && (
              <div className="flex items-center gap-3 bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                <Calendar className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                <div>
                  <p className="sous-titre-page">Date de demande</p>
                  <p className="text-sm font-semibold text-[var(--text-bold)]">{fmt(demande.date_demande)}</p>
                </div>
              </div>
            )}
            {demande.nom_organisation && (
              <div className="flex items-center gap-3 bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                <Building2 className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                <div>
                  <p className="sous-titre-page">Organisation</p>
                  <p className="text-sm font-semibold text-[var(--text-bold)]">{demande.nom_organisation}</p>
                </div>
              </div>
            )}
            {/* Identite recueillie par la page des tarifs. Ces encarts ne
                s'affichent que si la demande les porte : une demande de
                formation vise une personne et n'en a aucun. */}
            {demande.type_organisation_display && (
              <div className="flex items-center gap-3 bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                <Building2 className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                <div>
                  <p className="sous-titre-page">Type d'organisation</p>
                  <p className="text-sm font-semibold text-[var(--text-bold)]">{demande.type_organisation_display}</p>
                </div>
              </div>
            )}
            {demande.budget_annuel_display && (
              <div className="flex items-center gap-3 bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                <Wallet className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                <div>
                  <p className="sous-titre-page">Budget annuel</p>
                  <p className="text-sm font-semibold text-[var(--text-bold)]">{demande.budget_annuel_display}</p>
                </div>
              </div>
            )}
            {demande.localisation && (
              <div className="flex items-center gap-3 bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                <MapPin className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                <div>
                  <p className="sous-titre-page">Localisation</p>
                  <p className="text-sm font-semibold text-[var(--text-bold)]">{demande.localisation}</p>
                </div>
              </div>
            )}
          </div>

          {/* Reponse au demandeur */}
          <div>
            <p className="surtitre text-[var(--text-muted)] mb-2">
              Repondre au demandeur
            </p>
            {reponses.length > 0 && (
              <div className="space-y-2 mb-3">
                {reponses.map((rep, i) => (
                  <div key={rep.id || i} className="rounded-controle border border-[var(--accent)]/30 bg-[var(--accent)]/5 px-4 py-2.5">
                    <p className="surtitre text-[var(--accent-texte)] mb-1">
                      {rep.email_sent === false ? 'Non envoye' : 'Envoye'}
                      {rep.envoye_par ? ' - ' + rep.envoye_par : ''}
                    </p>
                    <p className="text-sm text-[var(--text-main)] whitespace-pre-line">{rep.message}</p>
                  </div>
                ))}
              </div>
            )}
            <textarea
              value={reponse}
              onChange={e => setReponse(e.target.value)}
              rows={3}
              placeholder={`Ecrire a ${demande.email || 'ce demandeur'}...`}
              className="champ resize-none"
            />
            <div className="flex items-center gap-3 mt-2">
              <button
                type="button"
                onClick={handleRepondre}
                disabled={envoiReponse || !reponse.trim() || !demande.email}
                className="act"
              >
                {envoiReponse ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                Envoyer le message
              </button>
              {infoReponse && (
                <span className={`text-[11px] font-bold ${infoReponse.type === 'ok' ? 'text-[var(--accent-texte)]' : infoReponse.type === 'warn' ? 'text-[var(--attention)]' : 'text-[var(--alerte)]'}`}>
                  {infoReponse.text}
                </span>
              )}
            </div>
          </div>

          {/* Message / description */}
          {demande.message && (
            <div>
              <p className="surtitre text-[var(--text-muted)] mb-2">Message</p>
              <div className="bg-[var(--bg-main)] rounded-pastille px-4 py-3 text-sm text-[var(--text-main)]">
                "{demande.message}"
              </div>
            </div>
          )}

          {/* Champs Formation spécifiques */}
          {typeService === 'formation' && (
            <div className="space-y-3">
              {demande.est_futur_coach !== undefined && (
                <div className="flex items-center gap-3 bg-[var(--bg-main)] rounded-pastille px-4 py-3">
                  <User className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                  <div>
                    <p className="sous-titre-page">Candidat coach</p>
                    <p className="text-sm font-semibold text-[var(--text-bold)]">
                      {demande.est_futur_coach ? 'Oui — souhaite devenir coach ASSO-PRO' : 'Non'}
                    </p>
                  </div>
                </div>
              )}
              {demande.modules_souhaites && (
                <div>
                  <p className="surtitre text-[var(--text-muted)] mb-2">Modules / domaines souhaités</p>
                  <div className="bg-[var(--bg-main)] rounded-pastille px-4 py-3 text-sm text-[var(--text-main)]">
                    {demande.modules_souhaites}
                  </div>
                </div>
              )}
            </div>
          )}

          {rattachee && (
            <div className="encart">
              Demande envoyée depuis l’espace de l’organisation, rattachée à son accompagnement en cours.
              L’accepter ajoute l’espace certification à ce projet : aucun nouveau dossier n’est créé.
            </div>
          )}

          {/* Zone notes admin */}
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-2">
              Notes admin
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Ajouter des notes internes sur cette demande..."
              className="champ resize-none"
              disabled={demande.statut !== 'en_attente'}
            />
          </div>

          {/* Formulaire d'acceptation (formation / certification) */}
          {showAcceptForm && demande.statut === 'en_attente' && (
            <div className="bg-[var(--accent)]/5 border border-[var(--accent)]/20 rounded-pastille p-5 space-y-4">
              <p className="surtitre text-[var(--accent-texte)]">
                Créer l'espace {typeService}
              </p>

              {/* Recherche organisation */}
              <div>
                <label className="block surtitre text-[var(--text-muted)] mb-2">
                  Organisation <span className="text-[var(--alerte)]">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={selectedOrg ? selectedOrg.nom : orgSearch}
                    onChange={e => { setOrgSearch(e.target.value); setSelectedOrg(null); setOrgDropdownOpen(true); }}
                    onFocus={() => setOrgDropdownOpen(true)}
                    placeholder="Rechercher l'organisation..."
                    className="champ"
                  />
                  {orgDropdownOpen && !selectedOrg && (
                    <div className="absolute z-10 w-full mt-1 bg-[var(--bg-card)] shadow-carte rounded-carte shadow-lg max-h-48 overflow-y-auto">
                      {orgsLoading ? (
                        <ChargeurLocal texte="Chargement des organisations…" className="p-3" />
                      ) : erreurListes ? (
                        <div className="p-3 text-sm text-[var(--alerte)] text-center" role="alert">
                          La liste des organisations n’a pas pu être chargée.{' '}
                          <button type="button" onClick={loadAcceptData} className="font-semibold underline">Réessayer</button>
                        </div>
                      ) : filteredOrgs.length === 0 ? (
                        <div className="p-3 text-sm text-[var(--text-muted)] text-center">
                          Aucune organisation ne correspond. Effacez le texte pour voir toute la liste.
                        </div>
                      ) : (
                        filteredOrgs.slice(0, 10).map(o => (
                          <button
                            key={o.id}
                            type="button"
                            onClick={() => { setSelectedOrg(o); setOrgDropdownOpen(false); }}
                            className="w-full text-left px-4 py-3 hover:bg-[var(--bg-main)] text-sm text-[var(--text-main)] border-b border-[var(--border)] last:border-0"
                          >
                            <span className="font-semibold">{o.nom}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {!selectedOrg && (
                  <p className="mt-2 text-[11px] font-semibold text-[var(--attention)]">
                    Cliquez dans le champ puis choisissez l'organisation dans la liste
                    pour activer la creation de l'espace.
                  </p>
                )}
                {selectedOrg && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-[var(--accent)]/10 rounded-pastille">
                    <CheckCircle2 className="w-4 h-4 text-[var(--accent-texte)] shrink-0" />
                    <span className="text-sm font-semibold text-[var(--text-bold)]">{selectedOrg.nom}</span>
                    <button type="button" onClick={() => { setSelectedOrg(null); setOrgSearch(demande.nom_organisation || ''); }}
                      className="ml-auto text-[var(--text-muted)] hover:text-[var(--alerte)]">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {/* Coach (optionnel) */}
              <div>
                <label className="block surtitre text-[var(--text-muted)] mb-2">
                  Coach assigné (optionnel)
                </label>
                <select
                  value={selectedCoach}
                  onChange={e => setSelectedCoach(e.target.value)}
                  className="champ"
                >
                  <option value="">— Aucun coach pour l'instant —</option>
                  {coaches.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nom_complet || c.utilisateur_display || c.id}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {error && (
            <div className="encart encart-alerte">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[var(--border)] flex flex-col sm:flex-row gap-3 shrink-0">
          <button
            onClick={onClose}
            className="act act-contour flex-1"
          >
            Fermer
          </button>
          {demande.statut === 'en_attente' && (
            <>
              <button
                onClick={() => handleAction('rejete')}
                disabled={!!loading}
                aria-busy={loading === 'rejete' || undefined}
                className="act act-danger flex-1"
              >
                {loading === 'rejete' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Rejeter
              </button>

              {depuisLEspace ? (
                <button
                  onClick={handleDemarrerProjet}
                  disabled={!!loading}
                  aria-busy={loading === 'accepter' || undefined}
                  className="act flex-1"
                >
                  {loading === 'accepter' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Accepter et démarrer le projet
                </button>
              ) : rattachee ? (
                <button
                  onClick={handleOuvrirCertification}
                  disabled={!!loading}
                  aria-busy={loading === 'accepter' || undefined}
                  className="act flex-1"
                >
                  {loading === 'accepter' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Accepter et ouvrir la certification
                </button>
              ) : canAcceptDirectly ? (
                showAcceptForm ? (
                  <button
                    onClick={handleAccepter}
                    disabled={!!loading || !selectedOrg}
                    aria-busy={loading === 'accepter' || undefined}
                    className="act flex-1"
                  >
                    {loading === 'accepter' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Créer l'espace
                  </button>
                ) : (
                  <button
                    onClick={handleOpenAccept}
                    className="act flex-1"
                  >
                    <Sparkles className="w-4 h-4" />
                    Accepter
                  </button>
                )
              ) : (
                <button
                  onClick={() => handleAction('traite')}
                  disabled={!!loading}
                  aria-busy={loading === 'traite' || undefined}
                  className="act flex-1"
                >
                  {loading === 'traite' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Marquer comme traité
                </button>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ── Page principale ────────────────────────────────────────────────────────────

const TYPE_TABS = [
  { key: '',               label: 'Toutes' },
  { key: 'formation',      label: 'Formation' },
  { key: 'accompagnement', label: 'Accompagnement' },
  { key: 'certification',  label: 'Certification' },
];

const STATUT_TABS = [
  { key: '',           label: 'En attente' },
  { key: 'traite',     label: 'Traitées' },
  { key: 'rejete',     label: 'Rejetées' },
];

// D'où vient la demande : affiché sur la fiche, pour savoir quoi en faire.
const ORIGINES = {
  tarifs: 'Page des tarifs',
  espace: "Depuis l'espace de l'organisation",
  contact_expert: '« Contacter un expert » (diagnostic)',
};

export default function Demandes() {
  // `?demande=<id>` : la notification ouvre directement la fiche du demandeur.
  const [params, setParams] = useSearchParams();
  const demandeOuverte = params.get('demande');
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  const dejaCharge = useRef(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [selected, setSelected] = useState(null);
  const [nbEnAttente, setNbEnAttente] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('type', filterType);
      const statutApi = filterStatut || 'en_attente';
      params.append('statut', statutApi);

      const [data, countData] = await Promise.all([
        adminApi.getDemandes(params.toString()),
        adminApi.getDemandes('statut=en_attente'),
      ]);
      setDemandes(Array.isArray(data) ? data : (data?.results || []));
      const countList = Array.isArray(countData) ? countData : (countData?.results || []);
      setNbEnAttente(countList.length);
      setError(null);
      dejaCharge.current = true;
      setCharge(true);
    } catch (e) {
      // Une actualisation qui échoue ne retire pas la liste affichée.
      if (dejaCharge.current) annoncerErreur(e.message || 'Les demandes n’ont pas pu être actualisées.');
      else setError(e);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatut]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!demandeOuverte) return;
    let annule = false;
    adminApi.getDemandeDetail(demandeOuverte)
      .then((d) => { if (!annule && d?.id) setSelected(d); })
      .catch((e) => { if (!annule) annoncerErreur(e.message || 'Cette demande est introuvable.'); });
    return () => { annule = true; };
  }, [demandeOuverte]);

  const fermer = () => {
    setSelected(null);
    if (demandeOuverte) {
      const suivants = new URLSearchParams(params);
      suivants.delete('demande');
      setParams(suivants, { replace: true });
    }
  };

  const handleUpdate = async (id, payload) => {
    await adminApi.updateDemande(id, payload);
    load();
  };

  const filtered = demandes.filter(d => {
    const q = search.toLowerCase();
    return (
      (d.nom || '').toLowerCase().includes(q) ||
      (d.nom_organisation || '').toLowerCase().includes(q) ||
      (d.email || '').toLowerCase().includes(q) ||
      // « toutes les demandes de labellisation » est une question qu'on se
      // pose : la recherche doit savoir y répondre.
      (d.formule_display || '').toLowerCase().includes(q)
    );
  });

  const fmt = (v) => v ? new Date(v).toLocaleDateString('fr-FR') : '—';

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>

      {/* Header */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-pastille bg-[var(--accent)]/15 flex items-center justify-center">
            <Inbox className="w-6 h-6 text-[var(--accent-texte)]" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="titre-page">Demandes de service</h1>
              {nbEnAttente > 0 && (
                <span className="px-2.5 py-1 rounded-controle bg-[var(--alerte-fond)] text-[var(--alerte)] text-[12px] font-semibold">
                  {nbEnAttente} en attente
                </span>
              )}
            </div>
            <p className="text-sm text-[var(--text-muted)]">
              Gestion des demandes de formation, accompagnement et certification
            </p>
          </div>
        </div>
      </div>

      {/* Tabs type */}
      {/* Les natures de demande : un choix exclusif, donc le controle
          segmente du modele plutot qu'une barre soulignee -- que rien ne
          distinguait d'une navigation vers une autre page. */}
      <div className="segmente max-w-full overflow-x-auto mb-4" role="group"
           aria-label="Filtrer par nature de demande">
        {TYPE_TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setFilterType(tab.key)}
            aria-pressed={filterType === tab.key}
            className="whitespace-nowrap shrink-0"
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filtres statut + recherche */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="segmente max-w-full overflow-x-auto" role="group"
             aria-label="Filtrer par statut">
          {STATUT_TABS.map(tab => (
            <button key={tab.key} type="button" onClick={() => setFilterStatut(tab.key)}
                    aria-pressed={filterStatut === tab.key}
                    className="whitespace-nowrap shrink-0">
              {tab.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Rechercher nom, organisation, email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="champ champ-loupe"
          />
        </div>
      </div>

      {/* Contenu */}
      {loading && !charge ? (
        <SqueletteTableau colonnes={6} />
      ) : error && !charge ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte"><EtatErreur erreur={error} onReessayer={load} /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          {search.trim() ? (
            <EtatSansDonnees cas="sansResultat" titre={`Aucun résultat pour « ${search.trim()} »`}
                             action={<button type="button" className="act act-sm act-contour" onClick={() => setSearch('')}>Effacer la recherche</button>} />
          ) : filterType || filterStatut ? (
            <EtatSansDonnees cas="sansFiltre"
                             action={<button type="button" className="act act-sm act-contour"
                                             onClick={() => { setFilterType(''); setFilterStatut(''); }}>Réinitialiser les filtres</button>} />
          ) : (
            <EtatVide
              icone={Inbox}
              titre="Aucune demande en attente"
              aide="Les demandes de formation, d’accompagnement et de certification arrivent ici dès leur envoi."
            />
          )}
        </div>
      ) : (
        <div className="carte-tableau">
          <div className="tableau-hote">
            <table className="tableau-cartes w-full">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  {['Type', 'Nom / Organisation', 'Contact', 'Date', 'Statut', 'Actions'].map(h => (
                    <th key={h} className="">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((d, i) => (
                  <tr
                    key={d.id}
                    className={`border-b border-[var(--border)] hover:bg-[var(--bg-main)] transition-colors ${i % 2 !== 0 ? 'bg-[var(--bg-main)]/30' : ''}`}
                  >
                    <td data-libelle="Type" className="px-5 py-4">
                      <TypeBadge type={d.type_service} />
                      {/* La formule sous le type : elle précise ce que le type
                          ne dit pas — « formation » ne distingue pas 10 000 de
                          60 000 FCFA. Discrète, car on ne l'ouvre pas pour
                          elle : on l'ouvre pour savoir qui demande quoi. */}
                      {d.formule_display && (
                        <p className="text-[11px] text-[var(--text-muted)] mt-1.5 leading-snug">
                          {d.formule_display}
                        </p>
                      )}
                    </td>
                    <td data-libelle="Nom / Organisation" className="px-5 py-4">
                      <p className="font-bold text-sm text-[var(--text-bold)]">{d.nom || '—'}</p>
                      <p className="text-xs text-[var(--text-muted)]">{d.nom_organisation || '—'}</p>
                    </td>
                    <td data-libelle="Contact" className="px-5 py-4">
                      {d.email && (
                        <div className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] mb-1">
                          <Mail className="w-3 h-3 shrink-0" />
                          {d.email}
                        </div>
                      )}
                      {d.telephone && (
                        <div className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
                          <Phone className="w-3 h-3 shrink-0" />
                          {d.telephone}
                        </div>
                      )}
                    </td>
                    <td data-libelle="Date" className="px-5 py-4 text-sm text-[var(--text-muted)] whitespace-nowrap">
                      {fmt(d.date_demande)}
                    </td>
                    <td data-libelle="Statut" className="px-5 py-4">
                      <StatutBadge statut={d.statut} />
                    </td>
                    <td data-actions="" className="px-5 py-4">
                      <button
                        onClick={() => setSelected(d)}
                        className="act act-icone act-tertiaire"
                        title="Voir le détail"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PiedTableau affiches={filtered.length} total={demandes.length}
                       nom="demandes" />
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {selected && (
          <DemandeModal
            demande={selected}
            onClose={fermer}
            onUpdate={handleUpdate}
            onReload={load}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
